import React, {useCallback, useEffect, useRef, useState} from 'react';
import { IView, LoadingProgress } from '@framework-controls/core';
import { strings } from 'src/localization';
import { Customer, RepairOrder, VehicleRec, ascClickToCallInput, ascCtaInteractionInput } from 'src/models';
import { ViewMode } from 'src/enums';
import VehicleStatus from './VehicleStatus/VehicleStatus';
import EApproval from './eApproval/eApproval';
import ReyPAY from './ReyPAY/ReyPAY';
import RepairService from './RepairService/RepairService';
import RepairAdvisor from './RepairAdvisor/RepairAdvisor';
import { IVehicleState, IRepairOrdersState } from 'src/store/states';
import * as css from './CurrentRepairCard.module.scss';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import moment from 'moment';
import MediaQuery from 'react-responsive';
import { ascClickToCall, ascCtaInteraction, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';
export interface IProps {
    viewMode?: ViewMode;
    repairOrders: IRepairOrdersState;
    customer: Customer;
    vehicles: IVehicleState;
    theme: string;
    locale: string;
    selectedVehicle?: string;
    pushToMainPanes: (inputView: IView) => void;
    fetchROs: (customerNum: string, roNum?: string) => void;
    selectRO: (roNum: string) => void;
    setLoadingROs: (loadingROs: boolean) => void;
    loadingROs: boolean;
    mainPaneViewOrder: IView[];
    addLogEntry: (action: string, screen: string, details: string) => void;
    getReyPAYUrl: () => void;
    getROHistory: () => void;
    clearROHistory: () => void;
    toggleROLangLits: (language: string) => void;
}

const CurrentRepairCard = (props: IProps) => {
    const [selectedRO, setSelectedRO] = useState<undefined | string>(undefined);
    const [sendLog, setSendLog] = useState(true);
    const displayDate = moment().format('MM/DD/YYYY');
    const timeVar = new Date().toLocaleTimeString('en-us', {hour: '2-digit', minute: '2-digit'});
    const timeDisplay = timeVar.replace(' ', '');
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;
    let repairOrder: RepairOrder | undefined = undefined;
    if (props.repairOrders.currentRepairOrder) {
        if (props.repairOrders.currentRepairOrder !== '-1') {
            repairOrder = props.repairOrders.RepairOrders.find(ro => ro.RONum === props.repairOrders.currentRepairOrder);
        }
    }
    let eApproval: null | JSX.Element = null;
    let reyPAY: null | JSX.Element = null;
    let vehicleStatus: null | JSX.Element = null;
    let repairServices: null | JSX.Element[] = null;
    let repairAdvisor: null | JSX.Element = null;

    useEffect(() => {
        if (props.repairOrders.currentRepairOrder && props.repairOrders.currentRepairOrder !== '-1' && sendLog) {
            props.addLogEntry('Current Repairs Viewed', '', '');
        }
    }, [
        props.addLogEntry,
        props.repairOrders.currentRepairOrder,
        sendLog,
    ]);

    // Filter repair orders by selected vehicle
    const fetchRORef = useRef(props.fetchROs);
    let vehicle: VehicleRec | undefined = undefined;
    vehicle = props.vehicles.vehicles.find(vehicle => vehicle.id === props.vehicles.selectedVehicle);
    if (vehicle && props.repairOrders.RepairOrders?.length > 0 && props.repairOrders.currentRepairOrder === '-1') {
        const repairOrders = props.repairOrders.RepairOrders.filter(
            repairOrder => {
                if (vehicle?.vin?.includes('*')) {
                    return repairOrder.vin?.slice(9) === vehicle?.vin?.slice(9);
                } else {
                    return repairOrder.vin === vehicle?.vin;
                }
            });
        // Use a map of repairOrders to find which RO date is the most recent and set that as the selected RO
        const date = Math.max(...repairOrders.map(ro => Date.parse(ro.RODate)));
        const selectRO = repairOrders.find(ro => Date.parse(ro.RODate) === date);
        if (selectRO && !selectedRO) {
            if (selectRO.RONum !== props.repairOrders.currentRepairOrder) {
                props.selectRO(selectRO.RONum);
                setSelectedRO(selectRO.RONum);
            }
        }
        if (selectRO && selectedRO !== selectRO.RONum) {
            props.selectRO(selectRO.RONum);
            setSelectedRO(selectRO.RONum);
        }
        if (selectRO && selectedRO === selectRO.RONum) {
            props.selectRO(selectRO.RONum);
        }
    }

    const name = props.customer?.firstName;
    const reyPayClickHandler = useCallback((e: any) => {
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.PayNow,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'selection_link',
            eventAction: 'click',
            eventActionResult: 'popup',
            linkUrl: props.repairOrders.reyPAYUrl,
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles.vehicles, props.selectedVehicle);
    }, [props.vehicles.vehicles, props.selectedVehicle, props.repairOrders.reyPAYUrl]);

    const phoneClick = useCallback((e: any) => {
        let commPhoneNumber = '';
        if (repairOrder?.advisorPhone) {
            commPhoneNumber = repairOrder?.advisorPhone;
            if (repairOrder?.advisorPhoneExt) {
                commPhoneNumber = `${repairOrder?.advisorPhone} ${repairOrder?.advisorPhoneExt}`;
            }
        }

        const ascClickToCallInp : ascClickToCallInput = {
            pageType: 'contact',
            commPhoneNumber: commPhoneNumber,
            department: 'service',
            affiliation: 'call_source',
        };
        ascClickToCall(ascClickToCallInp, props.vehicles.vehicles, props.selectedVehicle);
        const elementColor = getElementColor(e, 'color');
        let elementText = repairOrder?.advisorPhone;
        if (repairOrder?.advisorPhoneExt) {
            elementText = `${repairOrder?.advisorPhone} ${repairOrder?.advisorPhoneExt}`;
        }
        elementText = elementText ?? '';
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: elementText,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'selection_link',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles.vehicles, props.selectedVehicle);
    }, [props.vehicles.vehicles, props.selectedVehicle, repairOrder?.advisorPhone, repairOrder?.advisorPhoneExt]);

    // Only load the current repair card if we have a repair order, spec data, a customer name and vehicle.
    if (repairOrder && name && vehicle) {
        if (repairOrder.eApprovalLink && repairOrder.eApprovalTotalItemsCount && repairOrder.eApprovalTotalItemsCount > 0) {
            eApproval = (
                <EApproval
                    approved={repairOrder.eApprovalApprovedCount ?? 0}
                    declined={repairOrder.eApprovalDeclinedCount ?? 0}
                    total={repairOrder.eApprovalTotalItemsCount}
                    link={repairOrder.eApprovalLink}
                    name={name}
                    fetchROs={fetchRORef.current}
                    custId={props.customer.nameId}
                    currentRO={props.repairOrders.currentRepairOrder}
                    setLoadingROs={props.setLoadingROs}
                    loadingROs={props.loadingROs}
                    mainPaneViewOrder={props.mainPaneViewOrder}
                    setSendLog={setSendLog}

                />
            );
        }

        // Grab the ReyPay URL if one exists
        if (!props.repairOrders.reyPAYUrl) {
            props.getReyPAYUrl();
        }

        if (props.repairOrders.reyPAYUrl && (props.repairOrders.reyPAYUrl !== 'NONE') && repairOrder.ReyPAYStatus) {
            reyPAY = (
                <ReyPAY
                    status={repairOrder.ReyPAYStatus}
                    link={props.repairOrders.reyPAYUrl}
                    name={name}
                    clickHandler={reyPayClickHandler}
                    theme={props.theme}
                />
            );
        }

        if (repairOrder.advisorName) {
            repairAdvisor = <RepairAdvisor
                name={repairOrder.advisorName}
                phone={repairOrder.advisorPhone}
                phoneExt={repairOrder.advisorPhoneExt}
                imageURL={repairOrder.advisorImage}
                theme={props.theme}
                phoneClick={phoneClick} />;
        }

        if (repairOrder.dmsStatuses?.length > 0) {
            vehicleStatus = (
                <VehicleStatus
                    repairOrder={repairOrder}
                    theme={props.theme}
                    locale={props.locale}
                    toggleROLangLits={props.toggleROLangLits}
                />
            );
        } else {
            vehicleStatus = <div>
                <p className={css.repairOrderNum}>{strings.RepairOrderNum + props.repairOrders.currentRepairOrder}</p>
            </div>;
        }

        repairServices = repairOrder.ROServices?.map((service, index) => (
            <RepairService
                key={index}
                service={service}
                theme={props.theme}
                vehicles={props.vehicles.vehicles}
                selectedVehicle={props.selectedVehicle}
            />
        ));
        let vehicleString = vehicle.make;
        if (vehicle.model) {
            vehicleString = vehicle.model;
        }

        const summaryTitleClass = classNames(
            css.summaryTitle,
            {
                [css.summaryTitleBMW]: props.theme === 'bmw',
                [css.summaryTitleBuick]: props.theme === 'buick',
                [css.summaryTitleDodge]: props.theme === 'dodge',
                [css.summaryTitleFord]: props.theme === 'ford',
                [css.summaryTitleIN]: props.theme === 'infiniti',
                [css.summaryTitleMazda]: props.theme === 'mazda',
                [css.summaryTitleAcura]: props.theme === 'acura',
            },
        );

        let longLitFlag = '';
        if (strings.RefreshRepair.length > 28) {
            longLitFlag = 'B';
        }

        const timeStringsStyles = classNames(
            css.timeStrings,
            {
                [css.timeStringsBMW]: props.theme === 'bmw',
                [css.timeStringsFord]: props.theme === 'ford',
                [css.timeStringsIN]: props.theme === 'infiniti',

            },
        );

        const refreshHandler = (e: any) => {
            props.setLoadingROs(true);
            props.fetchROs(props.customer.nameId, props.repairOrders.currentRepairOrder);
            const ascCtaInteractionInp : ascCtaInteractionInput = {
                pageType: 'item',
                elementText: strings.RefreshRepair,
                elementColor: '',
                elementOrder: '',
                elementType: 'search_tool',
                elementSubType: 'other_buttons',
                eventAction: 'click',
                eventActionResult: 'search',
                linkUrl: '',
            };
            ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles.vehicles, props.selectedVehicle);
        };

        return (
            <div>
                <hr className={css.hr} />
                <h4 className={summaryTitleClass}>{strings.CurrentRepair}</h4>
                <p className={props.theme==='bmw' ? css.subheadingBMW:''}>
                    {strings.HereCurrentStatus + vehicleString + ':'}
                </p>
                {props.loadingROs ?
                    <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
                <div>
                    <MediaQuery minWidth={601}>
                        <div className={css.repairOrder}>
                            {vehicleStatus}
                            <div className={css.flexBox}>
                                {reyPAY}
                                {eApproval}
                            </div>
                            <div className={css.serviceDivider}>
                                {strings.ServicesOnRepair}
                            </div>
                            <div className={css.flexBox}>
                                {repairServices}
                            </div>
                            <div className={css.timeDisplay}>
                                <div className={timeStringsStyles}>{strings.AsOf2} {timeDisplay} {strings.On2} {displayDate}</div>
                            </div>
                            <div className={css.repairRefresh}>
                                {repairAdvisor}
                                <div className={css.refreshButton}>
                                    <SVPButton
                                        id='Proceed'
                                        handler={refreshHandler}
                                        message={strings.RefreshRepair}
                                        variant={'secondaryBtnAlt'}
                                        tooltip={strings.RefreshToolTip}
                                        theme={props.theme}
                                        noArrow={noArrow}
                                    />
                                </div>
                            </div>
                        </div>
                    </MediaQuery>
                    <MediaQuery maxWidth={600}>
                        <div className={css.repairOrder}>
                            {vehicleStatus}
                            <div className={css.flexBox}>
                                {reyPAY}
                                {eApproval}
                            </div>
                            <div className={css.serviceDivider}>
                                {strings.ServicesOnRepair}
                            </div>
                            <div className={css.flexBox}>
                                {repairServices}
                            </div>
                            <div className={css.timeDisplay}>
                                <div className={timeStringsStyles}>{strings.AsOf} {timeDisplay} {strings.On} {displayDate}</div>
                            </div>
                            <div className={css.repairRefresh}>
                                {repairAdvisor}
                            </div>
                        </div>
                        <div className={css.refreshButton}>
                            <SVPButton
                                id='Proceed'
                                handler={refreshHandler}
                                message={strings.RefreshRepair}
                                variant={'secondaryBtnAlt'}
                                tooltip={strings.RefreshToolTip}
                                longLitFlag={longLitFlag}
                                theme={props.theme}
                                noArrow={noArrow}
                            />
                        </div>
                    </MediaQuery>
                </div>
            </div>
        );
    }

    return null;
};


export default CurrentRepairCard;
